/**
 * Emerging hotspot analysis — pragmatic, fully-frontend implementation.
 *
 * For a visible point layer with a temporal column, we:
 *   1. Build a hexagonal space-time cube (hex grid × time bins).
 *   2. Per hex cell, count features per time bin → time series.
 *   3. Compute the Mann-Kendall Z statistic on the time series to classify
 *      the cell as Increasing / Decreasing / Stable / Sparse.
 *   4. Render the result as colored hex polygons.
 *
 * This is not the full ESRI 8-category emerging-hotspot product (no
 * Getis-Ord Gi* on the cube cells), but it captures the temporal
 * trend essence and runs entirely in the browser.
 */
import * as turf from '@turf/turf';
import maplibregl from 'maplibre-gl';
import { setLegend } from '../ui/legend.js';
import { toast } from '../ui/toast.js';

const COLORS = {
  increasing: '#d65a51',   // red — getting more
  decreasing: '#04648d',   // teal — getting less
  stable:     '#cfd6dc',   // gray — no trend
  sparse:     '#3a4750',   // dark — too few obs
};

const Z_THRESHOLD = 1.96;  // 95% two-sided
const MIN_OBS = 4;          // bins per cell needed to even attempt MK
const MAX_POINTS = 50000;   // bail out above this; client-side gets too slow

let userLayersRef = null;
let mapRef = null;
export function registerUserLayers(m) { userLayersRef = m; }
export function registerMap(m)        { mapRef = m; }

// ---------------------------------------------------------------------------
// Public action
// ---------------------------------------------------------------------------

export function openEmergingHotspotModal() {
  const visible = userLayersRef?.getVisibleVectorLayersWithFeatures?.() || [];
  const points = visible.filter(l => /Point/i.test(l.geometryType || ''));
  if (points.length === 0) {
    toast('Add a visible point layer first.');
    return;
  }

  document.querySelector('#ehotModal')?.remove();

  // Pick the first point layer that has a parseable date column to default to.
  let initialLayer = points[0];
  let initialCol = null;
  for (const lyr of points) {
    const cols = detectDateColumns(lyr.features);
    if (cols.length) {
      initialLayer = lyr;
      initialCol = cols[0];
      break;
    }
  }
  if (!initialCol) {
    initialCol = detectDateColumns(initialLayer.features)[0] || null;
  }

  const state = {
    layerId:    initialLayer.id,
    column:     initialCol,
    cellSizeKm: 25,
    binMode:    'auto',     // auto | year | month | day
  };

  document.body.insertAdjacentHTML('beforeend', shellHtml(points, state));
  const root = document.getElementById('ehotModal');

  // Refresh the date-column options whenever the layer changes
  const refreshCols = () => {
    const lyr = points.find(p => p.id === state.layerId);
    const cols = detectDateColumns(lyr?.features || []);
    const sel = root.querySelector('#ehotCol');
    if (!sel) return;
    sel.innerHTML = cols.length
      ? cols.map(c => `<option value="${escape(c)}" ${c === state.column ? 'selected' : ''}>${escape(c)}</option>`).join('')
      : `<option value="">(no temporal columns detected)</option>`;
    if (!cols.includes(state.column)) state.column = cols[0] || null;
  };

  root.querySelector('#ehotLayer').addEventListener('change', e => {
    state.layerId = +e.target.value;
    refreshCols();
  });
  root.querySelector('#ehotCol').addEventListener('change', e => { state.column = e.target.value; });
  root.querySelector('#ehotCell').addEventListener('input', e => {
    state.cellSizeKm = +e.target.value;
    root.querySelector('#ehotCellVal').textContent = `${state.cellSizeKm} km`;
  });
  root.querySelector('#ehotBin').addEventListener('change', e => { state.binMode = e.target.value; });

  refreshCols();

  root.addEventListener('click', e => {
    if (e.target === root || e.target.closest('[data-eh="cancel"]')) root.remove();
    if (e.target.closest('[data-eh="run"]'))  runAnalysis(state, points, root);
    if (e.target.closest('[data-eh="clear"]')) { clearHotspot(); root.remove(); }
  });
  document.addEventListener('keydown', escClose, { once: true });
  function escClose(e) { if (e.key === 'Escape' && document.getElementById('ehotModal')) root.remove(); }
}

export function clearHotspot() {
  mapRef?.getSource('emerging-hotspot')?.setData({ type: 'FeatureCollection', features: [] });
  toast('Emerging hotspot cleared');
}

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

const TEMPORAL_NAME = /(year|date|month|day|time|timestamp|created|updated|period)/i;

function detectDateColumns(features) {
  if (!features?.length) return [];
  const sample = features.slice(0, Math.min(features.length, 200));
  const props = sample[0]?.properties || {};
  const candidates = [];

  for (const key of Object.keys(props)) {
    const values = sample.map(f => f.properties?.[key]).filter(v => v != null && v !== '');
    if (values.length === 0) continue;

    const allFinite = values.every(v => Number.isFinite(+v));
    if (allFinite) {
      const nums = values.map(v => +v);
      const min = Math.min(...nums), max = Math.max(...nums);
      if (Number.isInteger(min) && Number.isInteger(max) && min >= 1800 && max <= 2200) {
        candidates.push({ name: key, kind: 'year', score: TEMPORAL_NAME.test(key) ? 5 : 3 });
        continue;
      }
    }

    const parsable = values.filter(v => !Number.isNaN(Date.parse(v))).length;
    if (parsable / values.length > 0.85) {
      candidates.push({ name: key, kind: 'date', score: TEMPORAL_NAME.test(key) ? 5 : 3 });
    }
  }
  candidates.sort((a, b) => b.score - a.score);
  return candidates.map(c => c.name);
}

function parseDate(value) {
  // Returns a JS timestamp in ms, or null.
  if (value == null || value === '') return null;
  if (typeof value === 'number' || (typeof value === 'string' && /^\d{4}$/.test(value.trim()))) {
    const n = Number(value);
    if (Number.isInteger(n) && n >= 1800 && n <= 2200) {
      return new Date(n, 0, 1).getTime();
    }
  }
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

// ---------------------------------------------------------------------------
// Run the analysis
// ---------------------------------------------------------------------------

function runAnalysis(state, points, root) {
  const lyr = points.find(p => p.id === state.layerId);
  if (!lyr || !state.column) {
    toast('Pick a layer and a temporal column.');
    return;
  }
  const features = lyr.features.filter(f => f.geometry?.type === 'Point');
  if (features.length === 0) {
    toast('No point features on this layer.');
    return;
  }
  if (features.length > MAX_POINTS) {
    toast(`Too many points for in-browser computation (${features.length.toLocaleString()} > ${MAX_POINTS.toLocaleString()}).`);
    return;
  }

  // 1. Parse dates and bbox
  const stamped = [];
  let lo = Infinity, hi = -Infinity;
  for (const f of features) {
    const t = parseDate(f.properties?.[state.column]);
    if (t == null) continue;
    stamped.push({ coord: f.geometry.coordinates, t });
    if (t < lo) lo = t;
    if (t > hi) hi = t;
  }
  if (stamped.length < MIN_OBS) {
    toast('Not enough features with valid dates to test a trend.');
    return;
  }

  // 2. Decide on bin granularity
  const binMode = state.binMode === 'auto' ? autoBinMode(lo, hi) : state.binMode;
  const binFn = binnerFor(binMode);

  // 3. Build hex grid over the layer's bbox
  const bboxArr = turf.bbox(turf.featureCollection(
    stamped.map(s => turf.point(s.coord))
  ));
  const grid = turf.hexGrid(bboxArr, state.cellSizeKm, { units: 'kilometers' });
  const cells = grid.features;

  // 4. Aggregate counts: cellIndex → Map<bin, count>
  const cellCounts = cells.map(() => new Map());
  for (const s of stamped) {
    // O(n_cells) per point — bbox prefilter would help if cells are many.
    for (let ci = 0; ci < cells.length; ci++) {
      const cell = cells[ci];
      const cb = turf.bbox(cell);
      if (s.coord[0] < cb[0] || s.coord[0] > cb[2] || s.coord[1] < cb[1] || s.coord[1] > cb[3]) continue;
      if (!turf.booleanPointInPolygon(turf.point(s.coord), cell)) continue;
      const k = binFn(s.t);
      cellCounts[ci].set(k, (cellCounts[ci].get(k) || 0) + 1);
      break;  // a point only falls into one cell
    }
  }

  // 5. Build a sorted list of all bin keys spanning the observed range
  const allBins = [];
  let cursor = floorBin(lo, binMode);
  const end = floorBin(hi, binMode);
  while (cursor <= end && allBins.length < 1000) {
    allBins.push(cursor);
    cursor = nextBin(cursor, binMode);
  }

  // 6. Per-cell Mann-Kendall classification
  const summary = { increasing: 0, decreasing: 0, stable: 0, sparse: 0 };
  const out = [];
  for (let ci = 0; ci < cells.length; ci++) {
    const counts = cellCounts[ci];
    if (counts.size === 0) continue;   // empty cell — skip rather than render

    const series = allBins.map(b => counts.get(b) || 0);
    const totalObs = series.reduce((s, v) => s + v, 0);
    let category;
    let z = null;

    if (counts.size < MIN_OBS || totalObs < MIN_OBS) {
      category = 'sparse';
    } else {
      z = mannKendallZ(series);
      if (z > Z_THRESHOLD)       category = 'increasing';
      else if (z < -Z_THRESHOLD) category = 'decreasing';
      else                       category = 'stable';
    }
    summary[category]++;

    const cell = cells[ci];
    cell.properties = {
      ...cell.properties,
      category,
      color: COLORS[category],
      total_count: totalObs,
      bins_with_data: counts.size,
      mk_z: z != null ? Math.round(z * 100) / 100 : null,
    };
    out.push(cell);
  }

  if (out.length === 0) {
    toast('No cells contained any features.');
    return;
  }

  mapRef.getSource('emerging-hotspot')?.setData({ type: 'FeatureCollection', features: out });

  // Fit and label
  const bounds = new maplibregl.LngLatBounds();
  out.forEach(c => turf.coordEach(c, p => bounds.extend(p)));
  if (!bounds.isEmpty()) mapRef.fitBounds(bounds, { padding: 60, duration: 1000, maxZoom: 11 });

  setLegend(`Emerging hotspot · ${lyr.name}`, [
    { color: COLORS.increasing, label: `Increasing (${summary.increasing})` },
    { color: COLORS.decreasing, label: `Decreasing (${summary.decreasing})` },
    { color: COLORS.stable,     label: `Stable (${summary.stable})` },
    { color: COLORS.sparse,     label: `Sparse (${summary.sparse})` },
  ]);

  toast(`${out.length} cells · ${binMode} bins · ${allBins.length} steps`);
  root.remove();
}

// ---------------------------------------------------------------------------
// Mann-Kendall (no-tie variance form — adequate for sparse counts)
// ---------------------------------------------------------------------------

function mannKendallZ(series) {
  const n = series.length;
  let s = 0;
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = series[j] - series[i];
      if (d > 0) s++;
      else if (d < 0) s--;
    }
  }
  if (s === 0) return 0;
  const variance = (n * (n - 1) * (2 * n + 5)) / 18;
  if (variance <= 0) return 0;
  const denom = Math.sqrt(variance);
  return s > 0 ? (s - 1) / denom : (s + 1) / denom;
}

// ---------------------------------------------------------------------------
// Time-bin helpers
// ---------------------------------------------------------------------------

function autoBinMode(loMs, hiMs) {
  const days = (hiMs - loMs) / (24 * 3600 * 1000);
  if (days >= 5 * 365) return 'year';
  if (days >= 365)     return 'month';
  return 'day';
}

function binnerFor(mode) {
  if (mode === 'year')  return (t) => new Date(t).getUTCFullYear();
  if (mode === 'month') return (t) => {
    const d = new Date(t);
    return d.getUTCFullYear() * 12 + d.getUTCMonth();
  };
  return (t) => Math.floor(t / (24 * 3600 * 1000));
}

function floorBin(t, mode) {
  return binnerFor(mode)(t);
}

function nextBin(b, mode) {
  return b + 1;
}

// ---------------------------------------------------------------------------
// Modal HTML
// ---------------------------------------------------------------------------

function shellHtml(points, state) {
  return `
    <div class="modal-bg show" id="ehotModal">
      <div class="modal" style="width:520px;max-width:96vw">
        <h3 style="margin:0">Emerging hotspot analysis</h3>
        <p style="font-size:11.5px;color:var(--text3);margin:6px 0 12px;line-height:1.5">
          Build a hexagonal space-time cube from a point layer + a date column. Each cell's count series is tested with the Mann-Kendall trend test and classified.
        </p>

        <div style="display:flex;flex-direction:column;gap:10px;margin-top:6px">
          <label style="font-size:11px;color:var(--text2)">
            Point layer
            <select id="ehotLayer" style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px">
              ${points.map(l => `<option value="${l.id}" ${l.id === state.layerId ? 'selected' : ''}>${escape(l.name)}</option>`).join('')}
            </select>
          </label>
          <label style="font-size:11px;color:var(--text2)">
            Temporal column
            <select id="ehotCol" style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px"></select>
          </label>
          <label style="font-size:11px;color:var(--text2)">
            Time bin
            <select id="ehotBin" style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px">
              <option value="auto"  ${state.binMode === 'auto'  ? 'selected' : ''}>Auto</option>
              <option value="year"  ${state.binMode === 'year'  ? 'selected' : ''}>Year</option>
              <option value="month" ${state.binMode === 'month' ? 'selected' : ''}>Month</option>
              <option value="day"   ${state.binMode === 'day'   ? 'selected' : ''}>Day</option>
            </select>
          </label>
          <label style="font-size:11px;color:var(--text2);display:flex;align-items:center;gap:10px">
            <span style="min-width:120px">Hex cell size</span>
            <input id="ehotCell" type="range" min="5" max="100" step="5" value="${state.cellSizeKm}" style="flex:1">
            <span id="ehotCellVal" style="font-family:var(--mono);font-size:11px;color:var(--text2);min-width:48px;text-align:right">${state.cellSizeKm} km</span>
          </label>
        </div>

        <div style="display:flex;justify-content:space-between;gap:8px;margin-top:18px">
          <button class="btn btn-sm" data-eh="clear">Clear result</button>
          <div style="display:flex;gap:8px">
            <button class="btn btn-sm" data-eh="cancel">Cancel</button>
            <button class="btn btn-sm btn-primary" data-eh="run">Run</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
